"use client";

import React from "react";
import { Cpu, PhoneCall, FileCheck, Wrench, Rocket, ArrowRight, Activity, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  id: "01",
  label: "THE TRADITIONAL GAP",
  heading: "Retainer Fatigue",
  description: "Traditional consultancies thrive on monthly retainers that reward slow progress and lack clear accountability for final outcomes.",
  icon: Activity,
  align: "left",
  colSpan: "md:col-span-5 md:col-start-2"
}, {
  id: "02",
  label: "THE PROJECTORA SHIFT",
  heading: "Milestone Execution",
  description: "We pivot to an outcome-based model. You only pay when specific, high-impact project milestones are successfully delivered.",
  icon: FileCheck,
  align: "right",
  colSpan: "md:col-span-5 md:col-start-7"
}, {
  id: "03",
  label: "RESOURCE INTEGRITY",
  heading: "Senior-Only Squads",
  description: "Eliminate the 'Junior Gap'. Every Projectora consultant is a senior lead with 10+ years of enterprise project management experience.",
  icon: Cpu,
  align: "left",
  colSpan: "md:col-span-5 md:col-start-2"
}, {
  id: "04",
  label: "TECHNICAL PRECISION",
  heading: "ServiceNow Mastery",
  description: "Deep technical expertise in ServiceNow implementation and migration, ensuring your architecture supports your business strategy.",
  icon: Wrench,
  align: "right",
  colSpan: "md:col-span-5 md:col-start-7"
}, {
  id: "05",
  label: "CERTAINTY OF DELIVERY",
  heading: "Guaranteed Outcomes",
  description: "A systematic approach to programme management that ensures every project meets its deadline, budget, and quality benchmarks.",
  icon: Rocket,
  align: "center",
  colSpan: "md:col-span-8 md:col-start-3"
}];
const protocolFeatures = [{
  title: "Auckland Seniority",
  description: "Local, boutique expertise. We don't outsource or use junior staff to learn on your budget.",
  icon: Cpu
}, {
  title: "Rapid ROI",
  description: "Eliminate hidden costs and project delays with our high-velocity delivery framework.",
  icon: TrendingUp
}];
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className={cn("transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", step.colSpan)}>
      <Card className="h-full py-8 gap-4 border-white/5 bg-[#112234] border-t-2 border-t-[#14b8a6] hover:border-[#14b8a6] transition-all duration-300">
        <CardHeader className="px-5 pb-0 gap-1">
          <div className="flex items-center justify-between mb-1">
            <Badge variant="outline" className="text-[10px] tracking-widest font-bold border-[#14b8a6] text-[#14b8a6] bg-transparent uppercase h-5">
              {step.label}
            </Badge>
            <span className="text-xl font-bold text-white/5 italic select-none">
              {step.id}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-[#14b8a6]/10 text-[#14b8a6]">
              <Icon className="size-4" />
            </div>
            <CardTitle className="text-xl font-bold tracking-tight text-white">
              {step.heading}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-5">
          <CardDescription className="text-sm text-white/70 leading-relaxed mt-0.5">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: protocolRef,
    isVisible: isProtocolVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="how-it-works" className="relative bg-[#0d1b2a] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#060f1a] to-transparent pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#060f1a] to-transparent pointer-events-none z-10" />
      
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-[20%] left-[-10%] w-[40%] h-[40%] bg-[#14b8a6]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[20%] right-[-10%] w-[40%] h-[40%] bg-[#14b8a6]/10 rounded-full blur-[120px]" />
      </div>

      <div className="py-12 md:py-16 relative z-20"> {}
        <div className="container relative mx-auto px-4">
          {}
          <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-10 md:mb-12 transition-all duration-700", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <Badge variant="outline" className="mb-3 border-[#14b8a6] bg-transparent text-[#14b8a6]">
              THE PROJECTORA METHOD
            </Badge>
            <h2 className="text-3xl md:text-6xl font-bold tracking-tight mb-8 text-white">
              Outcome-Based Delivery <br className="hidden md:block" />
              <span className="text-[#14b8a6]">Without the Retainer Risk</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed">
              We've dismantled the traditional consultancy model to focus exclusively on your success. 
              No idle billing, no junior staff, just senior execution at every milestone.
            </p>
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-y-6 md:gap-y-8 gap-x-6">
            {steps.map((step, i) => <StepCard key={step.id} step={step} index={i} />)}
          </div>

          {}
          <div ref={protocolRef} className={cn("mt-16 max-w-5xl mx-auto transition-all duration-1000", isProtocolVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="p-6 md:p-8 rounded-2xl border-t-2 border-[#14b8a6] bg-[#112234] relative overflow-hidden">
              <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">The Projectora Protocol</h3>
                  <p className="text-sm text-white/70 mb-6">
                    Our engagement framework is designed for high-stakes enterprise environments where certainty of delivery is the only metric that matters.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="sm" className="rounded-full px-6 h-10 bg-[#14b8a6] text-white hover:bg-[#14b8a6]/90 border-none">
                      <Link to="/MilestoneMethodologyPage">
                        View Methodology <ArrowRight className="ml-2 size-4 text-[#14b8a6]" />
                      </Link>
                    </Button>
                    <Button variant="outline" asChild size="sm" className="rounded-full px-6 h-10 border-[#14b8a6] text-[#14b8a6] bg-transparent hover:bg-[#14b8a6]/10">
                      <Link to="/ContactPage">Discuss a Project</Link>
                    </Button>
                  </div>
                </div>

                <div className="grid gap-4">
                  {protocolFeatures.map((feature, i) => <div key={i} data-index={i} className="flex gap-4 p-3 rounded-xl bg-white/5 border border-white/10">
                      <div className="shrink-0 size-10 rounded-lg bg-[#14b8a6]/10 flex items-center justify-center text-[#14b8a6]">
                        <feature.icon className="size-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-base mb-0.5 text-white">{feature.title}</h4>
                        <p className="text-xs text-white/70 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}